import { useState } from 'react';
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import type { BulkActionType } from '../types';
import { BULK_ACTION_LABELS as LABELS } from '../../../utils/labels';

interface BulkActionBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onAction: (action: BulkActionType) => void;
  onPickupConfirm: () => void;
  onPickupUnconfirm: () => void;
}

export default function BulkActionBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  onAction,
  onPickupConfirm,
  onPickupUnconfirm,
}: BulkActionBarProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const allSelected = selectedCount === totalCount && totalCount > 0;

  const handleMenuAction = (fn: () => void) => {
    setMenuAnchor(null);
    fn();
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-primary/10 border-b border-primary/20 flex-shrink-0">
      <span className="text-sm font-medium text-primary">
        {LABELS.bulkBanner(selectedCount, totalCount)}
      </span>

      <Button
        size="small"
        variant="text"
        color="primary"
        onClick={allSelected ? onDeselectAll : onSelectAll}
      >
        {allSelected ? LABELS.bulkDeselectAll : LABELS.bulkSelectAll}
      </Button>

      <div className="flex-1" />

      <Button
        variant="outlined"
        startIcon={<ChecklistIcon />}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        disabled={selectedCount === 0}
        aria-haspopup="true"
        aria-expanded={!!menuAnchor}
        aria-label={LABELS.bulkApply}
        data-testid="bulk-apply-button"
      >
        {LABELS.bulkApply}
      </Button>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => handleMenuAction(() => onAction('status'))} data-testid="bulk-action-status">
          <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{LABELS.bulkStatus}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction(onPickupConfirm)} data-testid="bulk-action-pickup-confirm">
          <ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText>{LABELS.bulkPickupConfirm}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction(onPickupUnconfirm)} data-testid="bulk-action-pickup-unconfirm">
          <ListItemIcon><RemoveCircleOutlineIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{LABELS.bulkPickupUnconfirm}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleMenuAction(() => onAction('start_date'))} data-testid="bulk-action-start-date">
          <ListItemIcon><CalendarTodayIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{LABELS.bulkStartDate}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction(() => onAction('end_date'))} data-testid="bulk-action-end-date">
          <ListItemIcon><EventIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{LABELS.bulkEndDate}</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
}
